import React, {
	createContext,
	type ReactNode,
	useState,
	useCallback,
	useContext,
	useMemo,
} from 'react';
import sortBy from 'lodash/sortBy';
import ArrowRightIcon from '@atlaskit/icon/core/migration/arrow-right';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import { COMMAND_PALETTE_SECTIONS } from '@atlassian/jira-command-palette-common/src/common/constants.tsx';
import { CommandShortcuts } from '@atlassian/jira-command-palette-common/src/ui/command-shortcuts/index.tsx';
import type { ActiveKeyMapWithCommandPalette } from '@atlassian/jira-command-palette-common/src/ui/command-shortcuts/types.tsx';
import { messages } from './messages.tsx';

export const TabShortcutContext = createContext({
	/**
	 * Registers a tab with TabShortcutsContextProvider so it can be assigned a keyboard shortcut
	 * returns a function to deregister.
	 */
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	registerTab: (_: TabShortcut) => () => {},
});

type Props = {
	children: ReactNode;
	navigationName?: string;
};

type TabShortcut = {
	callback: () => void;
	label?: string;
	/** The index the tab thinks they are. Some values returned from the server will not end up as tabs, so this number is not always correct, but it is useful for ordering.  */
	index?: number;
};

export const TabShortcutsContextProvider = ({ navigationName, children }: Props) => {
	const { formatMessage } = useIntl();
	const [rawTabs, setRawTabs] = useState<TabShortcut[]>([]);

	const registerTab = useCallback(
		(newTab: TabShortcut) => {
			setRawTabs((existingTabs) => [...existingTabs, newTab]);
			return () => {
				setRawTabs((existingTabs) => existingTabs.filter((tab) => tab.index !== newTab.index));
			};
		},
		[setRawTabs],
	);

	/**
	 * Generate a keyMap from the rawTabs. The tabs may be out of order, and there may be gaps in the index (some tabs will return null before they get to register)
	 * so we sort first and use our own index for the trigger key.
	 */
	const generateKeyMap = () => {
		const keyMap: ActiveKeyMapWithCommandPalette = {};

		sortBy(rawTabs, 'index')
			.slice(0, 9)
			.forEach((tab, index) => {
				keyMap[String(index + 1)] = {
					callback: tab.callback,
					label: <FormattedMessage {...messages.goTo} values={{ label: tab.label }} />,
					registerInCommandPalette: {
						id: `tab${index + 1}-${tab.label}`,
						name: formatMessage(messages.goTo, { label: tab.label }),
						components: {
							LeftIcon: () => <ArrowRightIcon spacing="spacious" label="" />,
						},
						section: COMMAND_PALETTE_SECTIONS.containerNavigation,
					},
				};
			});

		return keyMap;
	};
	const valueMemo = useMemo(() => ({ registerTab }), [registerTab]);

	return (
		<TabShortcutContext.Provider value={valueMemo}>
			{children}
			<CommandShortcuts
				keyMap={generateKeyMap()}
				label={
					navigationName ? (
						<>{navigationName}</>
					) : (
						<FormattedMessage {...messages.projectNavigation} />
					)
				}
			/>
		</TabShortcutContext.Provider>
	);
};

export const useTabShortcuts = () => {
	const context = useContext(TabShortcutContext);
	if (!context) {
		throw new Error('useTabShortcuts must be used within a TabShortcutsContextProvider');
	}
	return context;
};
